import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { DOCUMENT_VIEW_DIALOG } from "@/constants/testIds";
import { toast } from "sonner";

const MD_COMPONENTS = {
  h1: ({ children }) => <p className="font-heading text-lg font-bold text-charcoal mt-4 mb-2">{children}</p>,
  h2: ({ children }) => <p className="font-heading text-base font-bold text-charcoal mt-4 mb-1.5">{children}</p>,
  h3: ({ children }) => <p className="text-sm font-semibold text-charcoal mt-3 mb-1">{children}</p>,
  p: ({ children }) => <p className="text-sm text-charcoal/80 leading-relaxed mb-2">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-charcoal">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-5 text-sm text-charcoal/80 space-y-1 mb-2">{children}</ul>,
  li: ({ children }) => <li>{children}</li>,
  hr: () => <hr className="border-petroleum/10 my-3" />,
};

export function DocumentViewDialog({ open, onOpenChange, fetchDocUrl, pdfUrl, onViewed }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !fetchDocUrl) return;
    setLoading(true);
    setDoc(null);
    api
      .get(fetchDocUrl)
      .then(({ data }) => {
        setDoc(data);
        onViewed?.();
      })
      .catch(() => toast.error("No se pudo cargar el documento."))
      .finally(() => setLoading(false));
  }, [open, fetchDocUrl]);

  const handleDownload = async () => {
    try {
      const { data } = await api.get(pdfUrl, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc?.title || "documento"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={DOCUMENT_VIEW_DIALOG.dialog} className="bg-bone max-w-2xl">
        <DialogHeader>
          <DialogTitle>{doc?.title || "Documento"}</DialogTitle>
          <DialogDescription>Lee el documento completo antes de confirmar.</DialogDescription>
        </DialogHeader>
        <div className="bg-white rounded-xl border border-petroleum/10 h-[55vh] overflow-y-auto p-5">
          {loading && (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-petroleum animate-spin" />
            </div>
          )}
          {!loading && doc && <ReactMarkdown components={MD_COMPONENTS}>{doc.body_markdown}</ReactMarkdown>}
        </div>
        <Button data-testid={DOCUMENT_VIEW_DIALOG.downloadPdfButton} onClick={handleDownload} disabled={!doc} variant="outline" className="w-full rounded-full border-petroleum/30 text-petroleum">
          <Download className="w-4 h-4 mr-2" /> Descargar PDF
        </Button>
      </DialogContent>
    </Dialog>
  );
}
